'use client';

import React from 'react';
import { ShieldCheck, AlertCircle, Zap } from 'lucide-react';

interface LoginCardProps {
  loginEmail: string;
  setLoginEmail: (email: string) => void;
  loginPassword: string;
  setLoginPassword: (password: string) => void;
  loginError: string;
  loginLoading: boolean;
  handleLogin: (e: React.FormEvent) => void;
}

export const LoginCard: React.FC<LoginCardProps> = ({
  loginEmail,
  setLoginEmail,
  loginPassword,
  setLoginPassword,
  loginError,
  loginLoading,
  handleLogin,
}) => {
  return (
    <div className="min-h-screen bg-[#060811] flex flex-col justify-center items-center px-4 font-sans relative overflow-hidden text-slate-100">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-tr from-indigo-600/20 via-purple-600/10 to-pink-600/15 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-3xl bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 p-[1px] mx-auto mb-5 shadow-2xl shadow-indigo-500/30">
            <div className="w-full h-full bg-[#0A0D18] rounded-[23px] flex items-center justify-center text-indigo-400">
              <ShieldCheck className="w-8 h-8" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white">
            ScaleSutra <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Admin Platform</span>
          </h1>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-950/60 border border-indigo-800/50 rounded-full text-xs font-mono text-indigo-300 mt-2.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            board-admin.cognoflux.com
          </div>
        </div>

        <div className="bg-[#0B0F1E]/90 border border-slate-800/90 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl shadow-black/90">
          {loginError && (
            <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-xs text-rose-400 font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-extrabold text-slate-400 mb-2 uppercase tracking-wider">
                Superadmin Email
              </label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="admin@cognoflux.com"
                className="w-full bg-[#060811] border border-slate-800 rounded-2xl px-4 py-3.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-extrabold text-slate-400 mb-2 uppercase tracking-wider">
                Password
              </label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#060811] border border-slate-800 rounded-2xl px-4 py-3.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:opacity-90 disabled:opacity-50 text-white font-black text-sm rounded-2xl shadow-xl shadow-indigo-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {loginLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Authenticate Superadmin</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
